import React, {
  createContext,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CareerPageAPIResponse,
  CareerPageResponse,
  SectionOrder,
} from 'types/careers-page/types';
import { GET_CUSTOMER_CAREER_PAGE } from 'graphql/careers/queries';
import { useQuery } from 'hooks/sympl-query';
import {
  AcceptedSections,
  SectionType,
  SectionTypeLabels,
} from '@symplbe/sympl-components';
import { useParams } from 'react-router-dom';
import {
  CREATE_VERSION_HISTORY,
  FORCE_UPDATE_PAGE,
  UPDATE_PAGE_BRANDING,
  UPDATE_PAGE_SECTIONS,
  UPDATE_PAGE_SECTIONS_ORDER,
  UPDATE_PAGE_SEO,
  UPDATE_PAGE_STATUS,
} from 'graphql/careers/mutations';
import { useMutation } from 'hooks/sympl-mutation';
import {
  CareerPageProviderProps,
  CareerPageType,
  ForceUpdatePagePayload,
  PageParams,
  TActiveBlockType,
  TActiveEditedBlock,
  TAddSectionFn,
  UpdatePageBrandingPayload,
  UpdatePageSectionsOrderPayload,
  UpdatePageSectionsPayload,
  UpdatePageSeoPayload,
  UpdatePageStatusPayload,
} from 'types/careers-page/context';
import { isEqual, uniq } from 'lodash';
import { useToastNotifications } from 'hooks/notificationHooks';
import { ToastTypes } from 'types/notificationTypes';
import {
  filterDeletedOrder,
  filterDeletedSections,
  getContactCards,
  getDefaultSectionValues,
  getHiddenKeys,
} from 'utils/careers/section-helpers/sectionHelpers';
import useModalContext from 'hooks/context/modals-context';
import ConfirmActionModal from 'components/modals/confirm-action-modal/ConfirmActionModal';
import tw from 'twin.macro';
import CareerAddSectionModal from 'components/careers/career-add-section-modal/CareerAddSectionModal';
import { v4 as uuid } from 'uuid';
import { PageSettingTabKeys } from 'components/careers/page-settings-sidebar/PageSettingsSidebar';
import useJobTypesContext from 'hooks/context/job-types-context';
import { fireEvent } from 'utils/eventHelper';
import useNavigationContext from 'hooks/context/nav-context';
import { useAutoSave } from 'hooks/useAutoSave';
import { useUndoRedo } from 'hooks/useUndoRedo';
import { usePendingSave } from 'context/PendingSaveContext';
import { sendKeepaliveSave } from 'utils/keepaliveSave';
import {
  CareersContent,
  CareersWaiting,
  contentBody,
  contentPath,
  contentToStore,
  discardCareers,
  getCareersVersion,
  getCareersWaiting,
  publishCareers,
  saveCareersContent,
  takeCareersOffline,
} from 'api/careersAutoSave';

/** Scene 12's own words: why a publish, a discard or "Take offline" is refused. */
export const CAREERS_NOT_SAVED_YET = 'Your last changes are not saved yet.';
const HOW_OFTEN_TO_LOOK_MS = 30_000;

/** The page adds a footer by itself to a page that has none (SectionsRenderer). */
const hasItsFooter = (page: CareerPageResponse | undefined): boolean =>
  !!page &&
  !!page.order?.length &&
  !!page.sections &&
  Object.values(page.sections).some(
    (section) => section.type === SectionType.Footer
  ) &&
  page.order.some(({ sectionId }) => sectionId.includes('footer-'));

export const CareerPageContext = createContext<CareerPageType>(
  {} as CareerPageType
);

export const CareerPageProvider: FC<CareerPageProviderProps> = ({
  children,
}) => {
  const { pushModal, removeModal } = useModalContext();
  const { activeBrand } = useNavigationContext();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [versionHistoryModalOpen, setVersionHistoryModalOpen] =
    useState<boolean>(false);

  // used for vacancies section
  const { jobsAndSubjobTypes } = useJobTypesContext();

  const [updatedKeys, setUpdatedKeys] = useState<string[]>([]);
  const [deletedSectionIds, setDeletedSectionIds] = useState<string[]>([]);

  const [activePageSettingKey, setActivePageSettingKey] =
    useState<PageSettingTabKeys | null>(null);

  const [activeBlockSection, setActiveBlockSection] =
    useState<TActiveBlockType | null>(null);
  const [activeEditedBlock, setActiveEditedBlock] =
    useState<TActiveEditedBlock | null>(null);

  const { addToast } = useToastNotifications();

  const [pageConfig, setPageConfig] = useState<CareerPageResponse>();
  const [pageInitialConfig, setPageInitialConfig] =
    useState<CareerPageResponse>();
  const { customerPageId } = useParams<PageParams>();

  const [hiddenKeys, setHiddenKeys] = useState<string[]>([]);
  const [hiddenInitialKeys, setHiddenInitialKeys] = useState<string[]>([]);

  const [updatePageStatus, { loading: pageStatusLoading }] = useMutation<
    undefined,
    UpdatePageStatusPayload
  >(UPDATE_PAGE_STATUS);

  const [updatePageBranding, { loading: pageBrandingLoading }] = useMutation<
    undefined,
    UpdatePageBrandingPayload
  >(UPDATE_PAGE_BRANDING);

  const [updatePageSeo, { loading: pageSeoLoading }] = useMutation<
    undefined,
    UpdatePageSeoPayload
  >(UPDATE_PAGE_SEO);

  const [updatePageSections, { loading: pageSectionsLoading }] = useMutation<
    undefined,
    UpdatePageSectionsPayload
  >(UPDATE_PAGE_SECTIONS);

  const [updatePageSectionsOrder, { loading: pageSectionsOrderLoading }] =
    useMutation<undefined, UpdatePageSectionsOrderPayload>(
      UPDATE_PAGE_SECTIONS_ORDER
    );

  const [createVersionHistory] = useMutation<
    undefined,
    { pageId: number; input: {} }
  >(CREATE_VERSION_HISTORY);

  const [forceUpdatePage, { loading: forceUpdatePageLoading }] = useMutation<
    undefined,
    ForceUpdatePagePayload
  >(FORCE_UPDATE_PAGE);

  const addSection: TAddSectionFn = useCallback(
    (type: SectionType, customConfig?: Partial<AcceptedSections>) => {
      if (!pageConfig) return;

      const newSectionId = `${type}-${uuid()}`;
      const newOrder: SectionOrder = {
        sectionId: newSectionId,
        isHidden: false,
      };

      const defaultValues = getDefaultSectionValues(
        {
          sectionId: newSectionId,
          type,
        },
        pageConfig,
        customConfig,
        jobsAndSubjobTypes
      );

      setPageConfig((prev) => {
        const prevOrder = prev?.order ?? [];
        const prevSections = prev?.sections ?? {};

        const tempSections = {
          ...prevSections,
          [newSectionId]: defaultValues,
        };

        const tempOrder =
          prevOrder && prevOrder.length > 0
            ? [...prevOrder, newOrder]
            : [newOrder];

        if (tempOrder.length > 1) {
          const hasFooter = tempOrder.find((o) =>
            o.sectionId.includes('footer-')
          );
          // if footer is present, move it to the end
          if (hasFooter) {
            const footerIndex = tempOrder.findIndex((o) =>
              o.sectionId.includes('footer-')
            );
            const footerOrder = tempOrder[footerIndex];
            tempOrder.splice(footerIndex, 1);
            tempOrder.push(footerOrder);
          }
        }

        return {
          ...prev,
          sections: tempSections,
          order: tempOrder,
        } as CareerPageResponse;
      });

      logUpdatedKeys('sections');
      logUpdatedKeys('order');
    },
    [pageConfig]
  );

  const deleteSection = useCallback(
    (sectionId: string) => {
      // active section is set to null
      if (sectionId && sectionId === activeSection) {
        handleSetActiveSection(null);
      }

      // add to deleted section ids
      if (sectionId) {
        setDeletedSectionIds((prev) => [...prev, sectionId]);
      }

      // set updated fields for section and order
      logUpdatedKeys('sections');
      logUpdatedKeys('order');
    },
    [deletedSectionIds, setDeletedSectionIds]
  );

  const logUpdatedKeys = (key: string) => {
    const temp = updatedKeys.length > 0 ? [...updatedKeys, key] : [key];

    setUpdatedKeys((prev) => {
      const combined = [...prev, ...temp];
      return [...uniq(combined)];
    });
  };

  const handleUpdatePageStatus = useCallback(
    (newStatus) => {
      // TEMPORARY:
      setPageConfig((prev) => ({
        ...(prev as CareerPageResponse),
        status: newStatus,
      }));
      logUpdatedKeys('status');
    },
    [updatedKeys, pageConfig, updatePageStatus, pageStatusLoading]
  );

  const handleUpdatePageBranding = useCallback(
    (newBranding) => {
      // TEMPORARY:
      setPageConfig((prev) => ({
        ...(prev as CareerPageResponse),
        branding: {
          ...(prev as CareerPageResponse).branding,
          ...newBranding,
        },
      }));

      logUpdatedKeys('branding');
    },
    [updatedKeys, pageConfig, updatePageBranding, pageBrandingLoading]
  );

  const handleUpdatePageSeo = useCallback(() => {
    // TODO: update page seo
    // updatePageSeo()
  }, [pageConfig, updatePageSeo, pageSeoLoading]);

  const handleUpdatePageSections = useCallback(
    (sectionId, sectionUpdates: Partial<AcceptedSections>) => {
      if (pageConfig?.sections?.[sectionId]) {
        // From the page as it is NOW, not as this function last saw it: an
        // Undo of an order or a hidden section changes the page without
        // this function being made again, and writing its remembered page
        // back would put the undone step back (review of the slice).
        setPageConfig((prev) =>
          prev?.sections?.[sectionId]
            ? {
                ...prev,
                sections: {
                  ...prev.sections,
                  [sectionId]: {
                    ...prev.sections[sectionId],
                    ...sectionUpdates,
                  },
                },
              }
            : prev
        );

        logUpdatedKeys('sections');
      }
    },
    [updatedKeys, pageConfig?.sections, updatePageSections, pageSectionsLoading]
  );

  const handleUpdatePageSectionsOrder = useCallback(
    (newOrder) => {
      if (newOrder) {
        setPageConfig((prev) => ({
          ...(prev as CareerPageResponse),
          order: newOrder,
        }));
        setHiddenKeys(getHiddenKeys(newOrder));
        logUpdatedKeys('order');
      }
    },
    [updatedKeys, pageConfig, updatePageSectionsOrder, pageSectionsOrderLoading]
  );

  const handleForceUpdatePage = useCallback(() => {}, [
    pageConfig,
    forceUpdatePage,
    forceUpdatePageLoading,
  ]);

  const handlePageSettingKey = useCallback(
    (key: PageSettingTabKeys | null) => {
      if (key === activePageSettingKey) {
        setActivePageSettingKey(null);
      } else {
        setActivePageSettingKey(key);
      }
    },
    [activePageSettingKey]
  );

  const handleSetActiveEditedBlock = useCallback(
    (activeBlock: TActiveEditedBlock | null) => {
      if (activeEditedBlock?.blockId !== activeBlock?.blockId) {
        setActiveEditedBlock(activeBlock);
      } else {
        setActiveEditedBlock(null);
        setActiveBlockSection(null);
      }
    },
    [activeEditedBlock, activeBlockSection]
  );

  const handleSetActiveSection = useCallback(
    (sectionId: string | null, sectionBlockObj?: TActiveBlockType) => {
      const isSameSection = sectionId && activeSection === sectionId;

      if (!sectionId || (!sectionBlockObj && isSameSection)) {
        setActiveSection(null);
        setActiveBlockSection(null);
        setActiveEditedBlock(null);
      } else {
        setActiveSection(sectionId);
      }

      if (sectionBlockObj) {
        setActiveBlockSection(sectionBlockObj);
      } else {
        setActiveBlockSection(null);
        setActiveEditedBlock(null);
      }
    },
    [
      activeSection,
      activeBlockSection,
      setActiveBlockSection,
      setActiveBlockSection,
    ]
  );

  const handleEditSection = useCallback(
    (sectionId: string | null, sectionBlockObj?: TActiveBlockType) => {
      handleSetActiveSection(sectionId, sectionBlockObj);
    },
    [handleSetActiveSection]
  );

  const handleDeleteSection = useCallback(
    (sectionId: string) => {
      const sectionType = pageConfig?.sections[sectionId].type as SectionType;
      const modalId = 'delete-section-modal';
      pushModal({
        show: true,
        twclass: tw`sm:(pl-0 pt-[40px] pb-0 pr-0) max-h-[unset]`,
        children: (
          <ConfirmActionModal
            modalId={modalId}
            actionBtnPostion="right"
            header={
              <div tw="px-[100px]">
                Are you sure you want to delete
                <br />
                {`"${SectionTypeLabels[sectionType]}" section`} ?
              </div>
            }
            body="Deleting this section will remove it from the page."
            confirmButtonLabel="Delete"
            confirmButtonProps={{ variant: 'danger' }}
            confirmActionFn={() => deleteSection(sectionId)}
            cancelButtonLabel="Cancel"
          />
        ),
        key: modalId,
      });
    },
    [deletedSectionIds, setDeletedSectionIds, pageConfig]
  );

  const handleAddSection = useCallback(() => {
    const modalId = 'add-section-modal';
    pushModal({
      show: true,
      twclass: tw`sm:(px-8 py-8)`,
      children: (
        <CareerAddSectionModal
          onAddSection={(sectionType) => {
            addSection(sectionType);
            removeModal(modalId);
          }}
        />
      ),
      key: modalId,
    });
  }, [pageConfig]);

  const handleSave = useCallback(() => {
    const promises = updatedKeys.map((key) => {
      if (key === 'status' && pageConfig?.status) {
        return updatePageStatus({
          variables: {
            pageId: parseInt(customerPageId ?? `1`),
            input: {
              status: pageConfig?.status,
            },
          },
          onCompleted: () => {
            if (key === 'status' && pageConfig?.status === 'ACTIVE')
              fireEvent('activate_careers', {
                pageId: customerPageId,
                brand: pageConfig?.branding?.brand_name,
              });
          },
        });
      } else if (key === 'branding' && pageConfig?.branding) {
        return updatePageBranding({
          variables: {
            pageId: parseInt(customerPageId ?? `1`),
            input: {
              branding: {
                ...pageConfig?.branding,
              },
            },
          },
        });
      } else if (key === 'seo' && pageConfig?.seo) {
        return updatePageSeo({
          variables: {
            pageId: parseInt(customerPageId ?? `1`),
            input: {
              seo: {
                ...pageConfig?.seo,
              },
            },
          },
        });
      } else if (key === 'sections' && pageConfig?.sections) {
        const { sections } = pageConfig;
        const filteredSections = filterDeletedSections(
          sections,
          deletedSectionIds
        );
        return updatePageSections({
          variables: {
            pageId: parseInt(customerPageId ?? `1`),
            input: {
              sections: {
                ...filteredSections,
              },
            },
          },
        });
      } else if (key === 'order' && pageConfig?.order) {
        const { order } = pageConfig;
        const filteredOrder = filterDeletedOrder(order, deletedSectionIds);
        return updatePageSectionsOrder({
          variables: {
            pageId: parseInt(customerPageId ?? `1`),
            input: {
              order: [...filteredOrder],
            },
          },
        });
      }
    });

    Promise.allSettled(promises)
      .then(() => {
        fireEvent('update_careers', {
          pageId: customerPageId,
          brand: pageConfig?.branding?.brand_name,
        });
        createVersionHistory({
          variables: {
            pageId: parseInt(customerPageId ?? `1`),
            input: {},
          },
        });
        setUpdatedKeys([]);
        refetchCareerDataResponse();
        addToast({
          title: 'Career page successfully saved.',
          description: 'Your career page has been successfully saved.',
          type: ToastTypes.SUCCESS,
        });
      })
      .catch((err) => {
        addToast({
          title: 'Something went wrong',
          description: JSON.stringify(err, null, 2),
          type: ToastTypes.ERROR,
        });
      });
  }, [updatedKeys, pageConfig]);

  const {
    data: careerDataResponse,
    refetch: refetchCareerDataResponse,
    loading: isCareerDataLoading,
  } = useQuery<
    CareerPageAPIResponse,
    {
      pageId: string | number | undefined;
    }
  >(GET_CUSTOMER_CAREER_PAGE, {
    skip: !customerPageId || !activeBrand,
    variables: {
      pageId: customerPageId,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      setPageConfig(data.customerCareerPage);
      setPageInitialConfig(data.customerCareerPage);

      // always delete the deletedkeys sections
      // this is to prevent the deleted sections from showing up
      // during the refetch
      setDeletedSectionIds([]);
    },
  });

  useEffect(() => {
    // if empty response dont proceed
    if (!careerDataResponse?.customerCareerPage) return;

    setPageConfig(careerDataResponse.customerCareerPage);
    setPageInitialConfig(careerDataResponse.customerCareerPage);
    setLoadedFor(customerPageId ?? null);
    setLoads((count) => count + 1);
    const count = careerDataResponse.customerCareerPage.kept_version;
    if (customerPageId && typeof count === 'number') {
      builtOn.current.set(customerPageId, count);
    }
    overwriteOnce.current = false;

    if (careerDataResponse.customerCareerPage.order) {
      setHiddenKeys(getHiddenKeys(careerDataResponse.customerCareerPage.order));
      setHiddenInitialKeys(
        getHiddenKeys(careerDataResponse.customerCareerPage.order)
      );
    }
  }, [careerDataResponse]);

  /*
   * Auto-save (scenes 17 and 23). The page stores itself: its sections and
   * their order go to the server in one store, a moment after the person
   * stops. On an Active page the server keeps that; visitors see it only
   * after "Publish changes".
   *
   * `loadedFor` is the page the content on screen was loaded for: picking
   * another brand's page does not rebuild this screen at once.
   */
  const { setPending, registerAbandon, registerSaveNow, switching } =
    usePendingSave();
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [loads, setLoads] = useState(0);
  const [settledFor, setSettledFor] = useState<string | null>(null);
  const [waiting, setWaiting] = useState<CareersWaiting | null>(null);
  const pageNow = useRef<string | undefined>(customerPageId);
  pageNow.current = customerPageId;
  /**
   * D10: the count of stores this screen builds on, per page. It follows the
   * page as it was loaded and this screen's OWN stores, never the half-minute
   * look at what is waiting: that would quietly accept a colleague's change.
   */
  const builtOn = useRef(new Map<string, number>());
  /** "Keep mine": the next store of this page is marked as overwriting. */
  const overwriteOnce = useRef(false);
  const basedOnFor = (page: string) => ({
    version: builtOn.current.get(page) ?? null,
  });
  const stored = (page: string, answer: CareersWaiting) => {
    if (typeof answer.kept_version === 'number') {
      builtOn.current.set(page, answer.kept_version);
    }
  };

  const loadKey =
    customerPageId && loadedFor === customerPageId
      ? `${customerPageId}:${loads}`
      : null;
  /*
   * On opening, the screen itself gives an empty page its first sections and
   * an older page its footer. That is not the person's work: storing starts
   * from the page as it stands AFTER that, so merely opening a page stores
   * nothing and leaves nothing waiting.
   */
  const complete = hasItsFooter(pageConfig);
  useEffect(() => {
    if (loadKey && complete && settledFor !== loadKey) setSettledFor(loadKey);
  }, [loadKey, complete, settledFor]);
  const storesItself =
    !!loadKey &&
    settledFor === loadKey &&
    !!pageConfig &&
    pageConfig.status !== 'ARCHIVED';

  /** What is stored, as one text: the same page gives the same text. */
  const print = useMemo<string | null>(
    () =>
      storesItself && pageConfig
        ? JSON.stringify(
            contentToStore(
              pageConfig.sections,
              pageConfig.order,
              deletedSectionIds
            )
          )
        : null,
    [storesItself, pageConfig?.sections, pageConfig?.order, deletedSectionIds]
  );
  const storable = useMemo<CareersContent | undefined>(
    () => (print ? (JSON.parse(print) as CareersContent) : undefined),
    [print]
  );

  const storableNow = useRef<CareersContent | undefined>(undefined);
  storableNow.current = storable;

  /** The header follows what the server says, for the page still on screen. */
  const showWaiting = useCallback((page: string, answer: CareersWaiting) => {
    if (pageNow.current !== page) return;
    setWaiting(answer);
    setPageConfig((prev) =>
      prev && prev.status !== answer.status
        ? { ...prev, status: answer.status as CareerPageResponse['status'] }
        : prev
    );
  }, []);

  const {
    state: saveState,
    keptAt,
    hasPending,
    hasFailed,
    abandon,
    saveNow,
    isKept,
    refusal,
    retryRefused,
  } = useAutoSave<CareersContent>({
    value: storable,
    enabled: storesItself,
    identity: customerPageId ? `careers:${customerPageId}` : null,
    save: async (value, signal) => {
      const page = customerPageId as string;
      const answer = await saveCareersContent(page, value, signal, {
        ...basedOnFor(page),
        overwrite: overwriteOnce.current,
      });
      overwriteOnce.current = false;
      stored(page, answer);
      showWaiting(page, answer);
    },
    flushOnHide: (value) => {
      if (customerPageId)
        sendKeepaliveSave(
          contentPath(customerPageId),
          contentBody(value, basedOnFor(customerPageId)),
          'PUT'
        );
    },
    // The content on screen at a page change is the previous page's.
    flushOnIdentityChange: (previous, value) => {
      const page = String(previous ?? '').replace('careers:', '');
      if (page)
        sendKeepaliveSave(
          contentPath(page),
          contentBody(value, basedOnFor(page)),
          'PUT'
        );
    },
  });

  /** One undo step puts a whole earlier page back (D11, D13, D37). */
  const { canUndo, canRedo, undo, redo } = useUndoRedo<{
    print: string;
    sections: CareerPageResponse['sections'];
    order: SectionOrder[];
    deleted: string[];
  }>({
    value:
      print && pageConfig
        ? {
            print,
            sections: pageConfig.sections,
            order: pageConfig.order,
            deleted: deletedSectionIds,
          }
        : undefined,
    enabled: storesItself,
    identity: loadKey,
    same: (a, b) => a.print === b.print,
    apply: (earlier) => {
      setPageConfig((prev) =>
        prev
          ? { ...prev, sections: earlier.sections, order: earlier.order }
          : prev
      );
      setDeletedSectionIds(earlier.deleted);
      setHiddenKeys(getHiddenKeys(earlier.order));
      // A section that is being edited may not exist in the earlier page.
      setActiveSection((open) => {
        const there =
          !!open && !!earlier.sections[open] && !earlier.deleted.includes(open);
        if (!there) {
          setActiveBlockSection(null);
          setActiveEditedBlock(null);
        }
        return there ? open : null;
      });
    },
  });

  // What the sidebar asks before taking the person away.
  useEffect(() => {
    if (!storesItself) return undefined;
    setPending(hasPending && hasFailed);
    registerAbandon(abandon);
    registerSaveNow(saveNow, isKept);
    return () => {
      setPending(false);
      registerAbandon(null);
      registerSaveNow(null);
    };
  }, [
    storesItself,
    hasPending,
    hasFailed,
    setPending,
    registerAbandon,
    abandon,
    registerSaveNow,
    saveNow,
    isKept,
  ]);

  // What is waiting: on opening the page, and every half minute for what a
  // colleague changed meanwhile.
  useEffect(() => {
    setWaiting(null);
    if (!loadKey || !customerPageId) return undefined;
    const page = customerPageId;
    const look = () =>
      getCareersWaiting(page)
        .then((answer) => showWaiting(page, answer))
        // Not knowing is not shown as "nothing waiting": the last answer stays.
        .catch(() => undefined);
    look();
    const again = window.setInterval(look, HOW_OFTEN_TO_LOOK_MS);
    return () => window.clearInterval(again);
  }, [loadKey, customerPageId, showWaiting]);

  /** Whatever is still on its way is stored first; if it cannot be, nothing happens. */
  const storedFirst = useCallback(async () => {
    if (!(await saveNow())) throw new Error(CAREERS_NOT_SAVED_YET);
  }, [saveNow]);

  /** "Publish changes" on an Active page, "Publish page" on a Draft one. */
  const publishPage = useCallback(async () => {
    const page = customerPageId as string;
    const wasActive = pageConfig?.status === 'ACTIVE';
    await storedFirst();
    /*
     * What is ON SCREEN becomes public. The sections the screen added by
     * itself on opening (a new page's first set, an older page's footer)
     * were never the person's change, so nothing stored them: without this
     * a new page went live EMPTY (review of the slice). Storing the same
     * page twice changes nothing.
     */
    if (storableNow.current) {
      const answer = await saveCareersContent(
        page,
        storableNow.current,
        undefined,
        basedOnFor(page)
      ).catch(() => {
        // Also when the screen is out of date: nothing is published then.
        throw new Error(CAREERS_NOT_SAVED_YET);
      });
      stored(page, answer);
    }
    showWaiting(page, await publishCareers(page));
    const brand = pageConfig?.branding?.brand_name;
    if (!wasActive) fireEvent('activate_careers', { pageId: page, brand });
    fireEvent('update_careers', { pageId: page, brand });
  }, [customerPageId, pageConfig, storedFirst, showWaiting]);

  /** "Discard changes": the page goes back to what visitors see. */
  const discardPageChanges = useCallback(async () => {
    const page = customerPageId as string;
    await storedFirst();
    await discardCareers(page);
    abandon();
    window.location.reload();
  }, [customerPageId, storedFirst, abandon]);

  /** Scene 14, "Load theirs": the page is loaded afresh; mine is dropped. */
  const loadTheirs = useCallback(() => {
    overwriteOnce.current = false;
    abandon();
    // A moment later: the screen has let go by then, so the browser's own
    // "leave this page?" does not come up for work just given up.
    window.setTimeout(() => window.location.reload(), 100);
  }, [abandon]);
  /** Scene 14, "Keep mine": my whole page goes over theirs, knowingly. */
  const keepMine = useCallback(() => {
    overwriteOnce.current = true;
    retryRefused();
  }, [retryRefused]);

  const takePageOffline = useCallback(async () => {
    const page = customerPageId as string;
    await storedFirst();
    showWaiting(page, await takeCareersOffline(page));
  }, [customerPageId, storedFirst, showWaiting]);

  /**
   * An earlier version is put on the page as an ordinary change: one Undo
   * step, stored like any other, public only after a publish (D37).
   */
  const restoreVersion = useCallback(
    async (historyId: number) => {
      const page = customerPageId as string;
      const version = await getCareersVersion(page, historyId);
      if (pageNow.current !== page) return;
      setPageConfig((prev) => {
        if (!prev) return prev;
        // The open jobs are not part of a version: today's are shown.
        const jobs = (
          Object.values(prev.sections ?? {}).find(
            (section) => section.type === SectionType.Vacancies
          ) as { vacancies?: unknown } | undefined
        )?.vacancies;
        const sections: CareerPageResponse['sections'] = {};
        Object.entries(version.sections ?? {}).forEach(([id, section]) => {
          sections[id] =
            section.type === SectionType.Vacancies
              ? ({ ...section, vacancies: jobs ?? [] } as AcceptedSections)
              : section;
        });
        return { ...prev, sections, order: version.order ?? [] };
      });
      setDeletedSectionIds([]);
      setHiddenKeys(getHiddenKeys(version.order ?? []));
      setActiveSection(null);
      setActiveBlockSection(null);
      setActiveEditedBlock(null);
    },
    [customerPageId]
  );

  const isPageDirty = useMemo(() => {
    return (
      !isEqual(pageInitialConfig, pageConfig) ||
      deletedSectionIds.length > 0 ||
      !isEqual(hiddenKeys, hiddenInitialKeys)
    );
  }, [
    pageInitialConfig,
    pageConfig,
    deletedSectionIds,
    hiddenKeys,
    hiddenInitialKeys,
  ]);

  const generateInitialPage = useCallback(() => {
    if (!pageConfig) return;
    const { branding } = pageConfig;
    addSection(SectionType.Hero);
    addSection(SectionType.Feature, { imagePlacement: 'left' });
    addSection(SectionType.Feature, {
      imagePlacement: 'right',
      bgColor: undefined,
    });
    addSection(SectionType.Testimonials);
    addSection(SectionType.Carousel, { bgColor: undefined });
    addSection(SectionType.Vacancies);

    if (branding?.location || branding?.recruiter) {
      addSection(SectionType.Contact, {
        bgColor: undefined,
        ...getContactCards(branding?.location, branding?.recruiter),
      });
    }
  }, [pageConfig]);

  // for backwards support
  const generateFooterSection = useCallback(() => {
    if (!pageConfig) return;
    addSection(SectionType.Footer);
  }, [pageConfig]);

  return (
    <CareerPageContext.Provider
      value={{
        careerPageConfig: pageConfig,
        refetchCareerDataResponse,
        isCareerDataLoading,

        // used for checking which sections are currently being edited
        activeSection,

        // active section block setting key ( used for order )
        activeBlockSection,

        // page block settings key ( used for config )
        activeEditedBlock,

        // for checking deleted sections
        deletedSectionIds,

        // for checking of changed values
        isPageDirty,

        // page settings key
        activePageSettingKey,

        // loading states
        pageStatusLoading,
        pageBrandingLoading,
        pageSeoLoading,
        pageSectionsLoading,
        pageSectionsOrderLoading,
        forceUpdatePageLoading,

        // handlers
        handleUpdatePageStatus,
        handleUpdatePageBranding,
        handleUpdatePageSeo,
        handleUpdatePageSections,
        handleUpdatePageSectionsOrder,
        handleForceUpdatePage,
        handleAddSection,
        handleEditSection,
        handleDeleteSection,
        handlePageSettingKey,
        handleSetActiveEditedBlock,

        // handlers
        handleSave,

        generateInitialPage,
        generateFooterSection,

        versionHistoryModalOpen,
        setVersionHistoryModalOpen,

        // auto-save
        storesItself,
        saveState,
        keptAt,
        switching,
        canUndo,
        canRedo,
        undo,
        redo,
        waiting,
        publishPage,
        discardPageChanges,
        takePageOffline,
        restoreVersion,
        refusal,
        loadTheirs,
        keepMine,
      }}
    >
      {children}
    </CareerPageContext.Provider>
  );
};
